<template>
  <Draggable
    tag="ul"
    v-model="list"
    :group="group"
    @start="handleStartDrag"
    @end="handleEndDrag"
    :animation="200"
    handle=".handle"
    @change="handlerChange"
    item-key="id"
  >
    <template #item="{ element }">
      <li
        :style="`
          '--var-bg-hover': ${bgHover};
          '--var-bg-selected': ${bgSelected};
          '--var-icon-color': ${iconColor};
          '--var-bg': ${sidebarColor};
        `"
        :class="[
          element?.current ? 'outline-none ring-2 ring-white' : EMPTY_STRING,
          'group relative grid w-full  grid-cols-5 items-center gap-4 rounded-md mt-1 ml-2 text-sm font-normal text-white',
        ]"
        @click.stop="emit('navigate:to', sectionMenu, element)"
        @mouseenter="handlerMouseEnter"
        @mouseleave="handlerMouseLeave"
      >
        <button id="iconBars" class="hidden" :style="{ color: iconColor }">
          <Bars4Icon class="handle h-5 w-5 cursor-grab" />
        </button>
        <span
          :title="element?.name"
          class="col-span-3 col-start-2 cursor-pointer truncate text-white"
        >
          {{ element?.name }}
        </span>

        <button
          v-if="passport('delete-shortcuts')"
          id="iconTrash"
          class="hidden"
          :style="{ color: iconColor }"
        >
          <TrashIcon
            class="h-5 w-5 cursor-pointer"
            @click.stop="emit('open:modal:confirm', element)"
          />
        </button>
      </li>
    </template>
  </Draggable>
</template>

<script setup lang="ts">
import { Bars4Icon, TrashIcon } from "@heroicons/vue/24/outline";
import Draggable from "vuedraggable";
import { computed, inject, ref, watchEffect } from "vue";
import { reqPatchShortcut } from "@/common/helpers/api";
import useErrorHandler from "@/common/composables/useErrorHandler";
import tinycolor from "tinycolor2";
import { passportKey } from "@/common/plugins/passport";
import { sidebarColorKey, type ChangeEvent } from ".";
import { EMPTY_STRING } from "@/common/helpers/constants";
import type { SortableEvent } from "sortablejs";

const props = defineProps<{
  group: string;
  sectionMenu: SidebarParent;
  sectionMenuChildrens: SidebarChildren[];
}>();

const emit = defineEmits<{
  "update:menu-childrens": [items: SidebarChildren[]];
  "navigate:to": [item: SidebarParent, subItem: SidebarChildren];
  "open:modal:confirm": [element: SidebarChildren];
}>();

const drag = ref(false);
const errorHandler = useErrorHandler();
const list = ref<SidebarChildren[]>([]);
const passport = inject(passportKey, () => false);

const bgHover = computed(() => {
  const tcolor = tinycolor(sidebarColor.value);
  return tcolor.lighten(10);
});

const bgSelected = computed(() => {
  const tcolor = tinycolor(sidebarColor.value);
  return tcolor.darken(10);
});

const iconColor = computed(() => {
  const tcolor = tinycolor(sidebarColor.value);
  return tcolor.darken(40);
});

const sidebarColor = inject(
  sidebarColorKey,
  computed(() => EMPTY_STRING)
);

watchEffect(() => {
  list.value = props.sectionMenuChildrens;
});

async function handlerChange(ev: ChangeEvent<HTMLElement>) {
  try {
    await reqPatchShortcut({
      id: ev?.moved?.element?.id,
      sort: ev?.moved?.newIndex,
    });
    emit("update:menu-childrens", list.value);
  } catch (e) {
    await errorHandler?.handleApiExceptions(e);
  }
}

function handleStartDrag(ev: SortableEvent) {
  drag.value = true;

  const childrens = Array.from(ev.from.children) as HTMLElement[];
  childrens?.forEach((liEl) => {
    liEl.classList.remove("bg-sky-400");
    liEl.classList.remove("custom-class");
    liEl.classList.remove("draggabble");

    Array.from(liEl.children)?.forEach((child) => {
      if (["iconBars", "iconTrash"].includes(child.id))
        child.classList.add("hidden");
    });
  });
}

function handleEndDrag(ev: SortableEvent) {
  drag.value = false;

  if (ev?.newIndex) {
    ev.to.children?.[ev?.newIndex].classList.add("bg-sky-400");
    ev.to.children?.[ev.newIndex].classList.add("custom-class");
    ev.to.children?.[ev.newIndex].classList.add("draggabble");

    const childrens = Array.from(
      ev.to.children?.[ev.newIndex]?.children
    ) as HTMLElement[];
    childrens?.forEach((child) => {
      if (["iconBars", "iconTrash"].includes(child.id))
        child.classList.remove("hidden");
    });
  }
}

function handlerMouseEnter(ev: MouseEvent) {
  if (drag.value) return;
  const target = ev.target as HTMLElement;

  target.classList.add("bg-sky-400");
  target.classList.add("custom-class");
  target.classList.add("draggabble");

  Array.from(target.children)?.forEach((child) => {
    if (["iconBars", "iconTrash"].includes(child.id))
      child.classList.remove("hidden");
  });
}

function handlerMouseLeave(ev: MouseEvent) {
  const target = ev.target as HTMLElement;
  target.classList.remove("bg-sky-400");
  target.classList.remove("custom-class");
  target.classList.remove("draggabble");

  Array.from(target.children)?.forEach((child) => {
    if (["iconBars", "iconTrash"].includes(child.id))
      child.classList.add("hidden");
  });
}
</script>

<style scoped>
:root {
  --var-bg-hover: transparent;
  --var-bg-selected: transparent;
  --var-color-icon: transparent;
  --var-bg: transparent;
}

.draggabble {
  background-color: var(--var-bg-hover);
  color: var(--var-color-icon);
}

.custom-class {
  background-color: var(--var-bg);
}

.custom-class:hover {
  background-color: var(--var-bg-hover);
}

.custom-class:focus {
  background-color: var(--var-bg-hover);
}
</style>
